import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  // ReferenceArea / ReferenceLine drew the disabled alert-time band and marker.
  // Restore these imports alongside them.
} from 'recharts';
import { ThemePalette } from 'mui.theme';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { useAppSelector } from 'store/hook';
import { normalizeTagName } from './utils';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import {
  convertBasedOnUnitSystem,
  convertCentToFahr,
  convertKpaToPsi,
} from 'utils/helpers/unitSystem';
import type {
  MLInsight,
  SupportingEvidenceResponse,
  AirFilterEvidenceResponse,
  EvidenceDataPoint,
  ProcessedChartDataPoint,
  RechartsPayload,
  InsightRuleCondition,
  RelationalOperator,
} from 'types/ml-insights.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/**
 * Recharts positions points from the raw numeric `ts`, so a UTC epoch plots on a
 * UTC number line even when the tick labels are rendered in the display zone.
 * Shifting `ts` by the display offset puts positions and labels on one basis, so
 * a point lands under the tick that names it. The true instant is preserved in
 * `originalTimestamp` for tooltips and alert-time matching.
 */
function displayOffsetMs(utcMs: number, displayTimezone: string): number {
  if (Number.isNaN(utcMs) || !displayTimezone) return 0;
  return dayjs(utcMs).tz(displayTimezone).utcOffset() * 60 * 1000;
}

/**
 * Athena returns `ds` / `datetime` as naive strings ('2026-07-31 21:00:00') that
 * mean UTC but carry no offset. `new Date(...)` would read those as browser-local
 * time and land the point hours away, so parse them explicitly as UTC.
 */
function evidenceTimestampToUtcMs(rawTimestamp: string): number {
  if (!rawTimestamp) return NaN;
  return dayjs.utc(rawTimestamp).valueOf();
}

function normalizeEvidenceTagKey(name: string): string {
  return name.replace(/_/g, '').toLowerCase();
}

function evidenceRowMatchesChartTag(
  item: EvidenceDataPoint,
  chartTagName?: string
): boolean {
  if (chartTagName == null || chartTagName === '') return true;
  if (item.name == null || item.name === '') return true;
  const evidenceTag = normalizeEvidenceTagKey(item.name);
  const chartTag = normalizeEvidenceTagKey(chartTagName);
  return (
    evidenceTag === chartTag ||
    evidenceTag.includes(chartTag) ||
    chartTag.includes(evidenceTag)
  );
}

/** API may send epoch seconds (< 1e12) or ms. */
function sampleTimestampToUnixMs(sampleTimestamp: number | string): number {
  const n =
    typeof sampleTimestamp === 'number'
      ? sampleTimestamp
      : Number(sampleTimestamp);
  if (Number.isNaN(n)) return NaN;
  return n < 1e12 ? Math.round(n * 1000) : n;
}

/** API sends SI units: kPa for pressure, °C for temperature. */
type EvidenceQuantityKind = 'pressure' | 'temperature' | 'none' | 'binary';

function getEvidenceQuantityKind(
  insightValue: string,
  tagHint: string
): EvidenceQuantityKind {
  const insightLower = insightValue.toLowerCase();
  if (insightValue === 'transmission_inefficiency') return 'none';
  const hint = tagHint.toLowerCase();
  if (
    insightLower.includes('lubric') ||
    hint.includes('lubric') ||
    hint.includes('reservoir') ||
    hint.includes('lube')
  ) {
    return 'binary';
  }
  if (
    hint.includes('rpm') ||
    hint.includes('enginespeed') ||
    (hint.includes('speed') && !hint.includes('pressure'))
  ) {
    return 'none';
  }
  if (
    hint.includes('temperature') ||
    hint.includes('temp') ||
    hint.includes('coolant')
  ) {
    return 'temperature';
  }
  if (
    hint.includes('pressure') ||
    hint.includes('tpms') ||
    (hint.includes('filter') && hint.includes('differential'))
  ) {
    return 'pressure';
  }
  return 'pressure';
}

function valueForDisplayUnitSystem(
  rawMetricValue: number,
  kind: EvidenceQuantityKind,
  unitSystem: string
): number {
  if (kind === 'none' || kind === 'binary') return rawMetricValue;
  if (kind === 'pressure') {
    return convertBasedOnUnitSystem(
      convertKpaToPsi,
      rawMetricValue,
      unitSystem
    );
  }
  return convertBasedOnUnitSystem(
    convertCentToFahr,
    rawMetricValue,
    unitSystem
  );
}

function valueUnitLabel(
  kind: EvidenceQuantityKind,
  unitSystem: string
): string {
  if (kind === 'none' || kind === 'binary') return '';
  if (kind === 'temperature') {
    return unitSystem === 'metric' ? ' (°C)' : ' (°F)';
  }
  return unitSystem === 'metric' ? ' (kPa)' : ' (psi)';
}

interface SupportingEvidenceProps {
  data: MLInsight;
  evidence: SupportingEvidenceResponse | AirFilterEvidenceResponse | null;
  enableDarkTheme: boolean;
  chartTagName?: string;
  notificationConditions?: InsightRuleCondition[];
  suppressInternalLoader?: boolean;
  onPointClick?: () => void;
  showDetailChart?: boolean;
  onToggleDetailChart?: () => void;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value?: number;
    dataKey?: string;
    payload?: ProcessedChartDataPoint;
  }>;
  enableDarkTheme: boolean;
  onPointClick?: () => void;
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes;
  timezone: string;
  /** Same units as Y-axis, e.g. ` (kPa)` / ` (psi)` */
  valueUnitSuffix: string;
  evidenceQuantityKind: EvidenceQuantityKind;
}

/**
 * Predicted-bound series colour. Emerald reads as a distinct signal against the
 * amber reading rather than a tint of it, and unlike a blue or violet it does
 * not sit near the navy chart ground, so the line keeps its edge in dark mode
 * while staying legible on a light background.
 */
const BOUND_COLOR = '#34D399';

/**
 * Fill for the reading-to-bound margin. Kept translucent so the amber area and
 * grid stay readable through it.
 */
const GAP_COLOR = '#EF4444';

const CustomTooltip: React.FC<TooltipProps> = ({
  active,
  payload,
  enableDarkTheme,
  preferredTimeDisplayMode,
  timezone,
  valueUnitSuffix,
  evidenceQuantityKind,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    // The bound series renders first, so payload[0] is not necessarily the
    // reading — pick the reading entry explicitly and fall back to the first.
    const data =
      payload.find((entry) => entry.dataKey === 'value') ?? payload[0];
    // Read both from the shared point, so a bound-only timestamp still shows
    // its bound even though the reading series has no entry there.
    const reading = data.payload?.value;
    const bound = data.payload?.bound;

    // `ts` already carries the selected-timezone offset that positions the point,
    // so format it as-is. Deriving the label from the same value the axis uses
    // keeps the tooltip and the tick underneath it in agreement.
    const pointTs = data.payload?.ts;
    const convertedTime =
      pointTs != null && !Number.isNaN(pointTs)
        ? dayjs.utc(pointTs).format('MM/DD/YYYY HH:mm:ss')
        : convertUtcToDisplayTime(
            data.payload?.originalTimestamp ?? '',
            preferredTimeDisplayMode,
            timezone
          );
    return (
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.tableHeader
            : palette.toolBarBackground,
          padding: '10px',
          borderRadius: '5px',
          minWidth: '200px',
        }}
      >
        <Typography sx={{ fontSize: '12px' }}>{convertedTime}</Typography>
        {reading !== undefined && (
          <Typography sx={{ fontSize: '12px' }}>
            {t('value')}{' '}
            {evidenceQuantityKind === 'binary'
              ? Math.round(reading)
              : reading.toFixed(2)}
            {valueUnitSuffix}
          </Typography>
        )}
        {bound !== undefined && (
          <Typography sx={{ fontSize: '12px', color: BOUND_COLOR }}>
            {t('predicted_bound')}{' '}
            {evidenceQuantityKind === 'binary'
              ? Math.round(bound)
              : bound.toFixed(2)}
            {valueUnitSuffix}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

export const SupportingEvidence: React.FC<SupportingEvidenceProps> = ({
  data,
  evidence,
  enableDarkTheme,
  chartTagName,
  notificationConditions,
  suppressInternalLoader,
  onPointClick,
  showDetailChart,
  onToggleDetailChart,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { isEvidenceLoading } = useAppSelector(
    (state) => state.mlInsightsReducer
  );

  const { timezone } = useTimeDisplayMode();

  const unitSystem =
    useAppSelector((state) => state.authReducer.customer?.unitSystem) ??
    'metric';
  // Anchored the disabled alert-time band and marker; restore alongside them.
  // const alertStartTs = data.alertStartTs ?? data.timestamp ?? '';
  const sourceValue = String(data.source ?? '').toLowerCase();
  const shouldApplyNotificationDotColors = sourceValue === 'notification';
  // Only gated the disabled anomaly alert-time marker; restore alongside it.
  // const shouldApplyMlNotificationDotColors = sourceValue === 'mlnotification';
  const insightValue = data.insight ?? data.issue ?? data.task ?? '';
  const tagNames = data.tagNames?.filter(Boolean) ?? [];
  const yAxisMetricLabel =
    chartTagName != null && chartTagName !== ''
      ? normalizeTagName(chartTagName)
      : tagNames.length > 0
      ? tagNames.map((t) => normalizeTagName(t)).join(' / ')
      : normalizeTagName(insightValue);

  const chartTitle =
    chartTagName != null && chartTagName !== ''
      ? `Supporting Evidence · ${normalizeTagName(chartTagName)}`
      : 'Supporting Evidence';

  const chartData = evidence ?? null;
  const tagHintForQuantity =
    chartTagName != null && chartTagName !== ''
      ? chartTagName
      : tagNames.length > 0
      ? tagNames.join(' ')
      : insightValue;

  const evidenceQuantityKind = getEvidenceQuantityKind(
    insightValue,
    tagHintForQuantity
  );

  const processChartData = (
    rawData: EvidenceDataPoint[],
    filterChartTagName: string | undefined,
    quantityKind: EvidenceQuantityKind,
    displayUnitSystem: string
  ): ProcessedChartDataPoint[] => {
    if (!rawData || !Array.isArray(rawData)) return [];

    const rows = rawData
      .filter((item) => evidenceRowMatchesChartTag(item, filterChartTagName))
      .map((item: EvidenceDataPoint): ProcessedChartDataPoint | null => {
        if (insightValue === 'transmission_inefficiency') {
          if (
            item.output_ratio === undefined ||
            item.output_ratio === null ||
            item.output_ratio === 'NODATA'
          ) {
            return null;
          }
          const utcMs =
            item.sample_timestamp != null
              ? sampleTimestampToUnixMs(item.sample_timestamp)
              : item.ds
              ? evidenceTimestampToUtcMs(item.ds)
              : evidenceTimestampToUtcMs(item.datetime ?? '');
          const raw = Number(item.output_ratio);
          if (Number.isNaN(utcMs) || Number.isNaN(raw)) return null;
          const ts = utcMs + displayOffsetMs(utcMs, timezone);
          return {
            ts,
            originalTimestamp:
              item.sample_timestamp != null
                ? new Date(utcMs).toISOString()
                : item.ds ?? item.datetime ?? '',
            value: valueForDisplayUnitSystem(
              raw,
              quantityKind,
              displayUnitSystem
            ),
            formattedDate:
              item.sample_timestamp != null
                ? dayjs(ts).format('MM DD HH:mm')
                : item.ds
                ? dayjs(item.ds).format('MM DD HH:mm')
                : dayjs(item.datetime ?? '').format('MM DD HH:mm'),
          };
        }

        if (
          item.sample_timestamp != null &&
          item.value !== undefined &&
          item.value !== null &&
          item.value !== 'NODATA'
        ) {
          const utcMs = sampleTimestampToUnixMs(item.sample_timestamp);
          const raw = Number(item.value);
          if (Number.isNaN(utcMs) || Number.isNaN(raw)) return null;
          const iso = new Date(utcMs).toISOString();
          const ts = utcMs + displayOffsetMs(utcMs, timezone);
          return {
            ts,
            originalTimestamp: iso,
            value: valueForDisplayUnitSystem(
              raw,
              quantityKind,
              displayUnitSystem
            ),
            formattedDate: dayjs(ts).format('MM DD HH:mm'),
          };
        }
        if (item.y_raw !== undefined) {
          // The reading drives the point: rows without a usable `y_raw` are
          // dropped even when they carry a bound, so the forecast never extends
          // the chart past the measured samples.
          if (item.y_raw === 'NODATA' || item.y_raw === null) return null;
          const utcMs = item.ds
            ? evidenceTimestampToUtcMs(item.ds)
            : evidenceTimestampToUtcMs(item.datetime ?? '');
          if (Number.isNaN(utcMs)) return null;
          const raw = Number(item.y_raw);
          const ts = utcMs + displayOffsetMs(utcMs, timezone);
          // The predicted bound shares the reading's unit, so it needs the same
          // conversion. Absent/null/non-numeric bounds stay undefined, which
          // makes the bound series skip that point instead of dropping to zero.
          const rawBound =
            item.yhat_bound === undefined ||
            item.yhat_bound === null ||
            item.yhat_bound === 'NODATA'
              ? undefined
              : Number(item.yhat_bound);
          const bound =
            rawBound === undefined || Number.isNaN(rawBound)
              ? undefined
              : valueForDisplayUnitSystem(
                  rawBound,
                  quantityKind,
                  displayUnitSystem
                );
          const displayValue = valueForDisplayUnitSystem(
            raw,
            quantityKind,
            displayUnitSystem
          );
          /**
           * The shaded margin spans reading-to-bound. Recharts can only stack
           * upwards, so express it as an invisible baseline at the lower of the
           * two plus the distance between them. `bound_type` names which side
           * the bound sits on: 'upper' shades bound - reading, anything else
           * (a lower bound) shades reading - bound.
           */
          const gapBase =
            bound === undefined ? undefined : Math.min(displayValue, bound);
          const gapSpan =
            bound === undefined ? undefined : Math.abs(bound - displayValue);
          return {
            ts,
            originalTimestamp: item.ds ?? item.datetime ?? '',
            value: displayValue,
            bound,
            boundType: item.bound_type ?? undefined,
            gapBase,
            gapSpan,
            formattedDate: item.ds
              ? dayjs(item.ds).format('MM DD HH:mm')
              : dayjs(item.datetime ?? '').format('MM DD HH:mm'),
          };
        }
        if (item.diff !== undefined && item.diff !== null) {
          const utcMs = item.ds
            ? evidenceTimestampToUtcMs(item.ds)
            : evidenceTimestampToUtcMs(item.datetime ?? '');
          if (Number.isNaN(utcMs)) return null;
          const raw = Number(item.diff);
          const ts = utcMs + displayOffsetMs(utcMs, timezone);
          return {
            ts,
            originalTimestamp: item.ds ?? item.datetime ?? '',
            value: valueForDisplayUnitSystem(
              raw,
              quantityKind,
              displayUnitSystem
            ),
            formattedDate: item.ds
              ? dayjs(item.ds).format('MM DD HH:mm')
              : dayjs(item.datetime ?? '').format('MM DD HH:mm'),
          };
        }
        return null;
      })
      .filter((p): p is ProcessedChartDataPoint => p !== null)
      .sort((a, b) => a.ts - b.ts);

    return rows;
  };

  const evidenceValuesRaw = chartData?.evidenceValues;
  const processedChartData = Array.isArray(evidenceValuesRaw)
    ? processChartData(
        evidenceValuesRaw,
        chartTagName,
        evidenceQuantityKind,
        unitSystem
      )
    : [];

  // Only mount the bound series when at least one point carries a bound —
  // otherwise the legend and axis pick up an all-empty series.
  const hasBoundSeries = processedChartData.some(
    (point) => point.bound !== undefined
  );

  // SVG pattern ids are document-global, so scope this one to the instance —
  // several of these charts render together on the per-tag details view.
  const gapPatternId = React.useId();

  // Recharts' default tick fill is too dim against the dark chart ground.
  const axisTickColor = enableDarkTheme ? '#FFFFFF' : palette.textPrimary;
  const axisLineColor = enableDarkTheme
    ? 'rgba(255, 255, 255, 0.45)'
    : palette.cartesianGrid;

  const valueUnitSuffix = valueUnitLabel(evidenceQuantityKind, unitSystem);

  const evaluateExpression = (
    value: number,
    operator: RelationalOperator,
    comparisonValue: number
  ) => {
    switch (operator) {
      case '>':
        return value > comparisonValue;
      case '<':
        return value < comparisonValue;
      case '>=':
        return value >= comparisonValue;
      case '<=':
        return value <= comparisonValue;
      case '=':
        return value === comparisonValue;
      case '!=':
        return value !== comparisonValue;
      default:
        return false;
    }
  };

  const getConditionsForChartTag = (): InsightRuleCondition[] => {
    if (!notificationConditions || notificationConditions.length === 0) {
      return [];
    }
    const chartTag =
      chartTagName && chartTagName.trim().length > 0
        ? chartTagName
        : tagNames[0] ?? '';
    if (!chartTag) {
      return notificationConditions;
    }
    const normalizedChartTag = normalizeEvidenceTagKey(chartTag);
    return notificationConditions.filter((condition) => {
      const normalizedConditionTag = normalizeEvidenceTagKey(
        String(condition.tagName ?? '')
      );
      return (
        normalizedConditionTag === normalizedChartTag ||
        normalizedConditionTag.includes(normalizedChartTag) ||
        normalizedChartTag.includes(normalizedConditionTag)
      );
    });
  };
  const matchedConditionsForChartTag = getConditionsForChartTag();

  const doesPointMatchRuleCondition = (pointValue?: number): boolean => {
    if (pointValue == null || Number.isNaN(pointValue)) return false;
    if (matchedConditionsForChartTag.length === 0) return false;
    return matchedConditionsForChartTag.every((condition) => {
      const threshold = conditionValueInDisplayUnit(condition);
      if (Number.isNaN(threshold)) return false;
      return evaluateExpression(
        pointValue,
        condition.relationalOperator,
        threshold
      );
    });
  };

  const conditionValueInDisplayUnit = (
    condition: InsightRuleCondition
  ): number => {
    const rawThreshold = Number(condition.value);
    if (Number.isNaN(rawThreshold)) return rawThreshold;
    return valueForDisplayUnitSystem(
      rawThreshold,
      evidenceQuantityKind,
      unitSystem
    );
  };

  /**
   * The sample that marks the alert start. Evidence samples sit on their own
   * cadence and rarely land exactly on the alert timestamp, so match the nearest
   * one within a tolerance instead of requiring millisecond equality — otherwise
   * no point is highlighted at all. `ts` carries the display offset, so the alert
   * time is shifted the same way before comparing.
   */
  // Disabled with the alert-time marker below — kept so the marker can be
  // restored without re-deriving the nearest-point search.
  // const alertMarkerTs = React.useMemo(() => {
  //   if (!shouldApplyMlNotificationDotColors) return null;
  //   const alertUtcMs = evidenceTimestampToUtcMs(alertStartTs);
  //   if (!alertStartTs || Number.isNaN(alertUtcMs)) return null;
  //   const alertDisplayTs = alertUtcMs + displayOffsetMs(alertUtcMs, timezone);
  //   const toleranceMs = 30 * 60 * 1000;
  //
  //   let nearestTs: number | null = null;
  //   let nearestGap = Number.POSITIVE_INFINITY;
  //   processedChartData.forEach((point) => {
  //     const gap = Math.abs(point.ts - alertDisplayTs);
  //     if (gap < nearestGap) {
  //       nearestGap = gap;
  //       nearestTs = point.ts;
  //     }
  //   });
  //   return nearestGap <= toleranceMs ? nearestTs : null;
  // }, [
  //   processedChartData,
  //   alertStartTs,
  //   timezone,
  //   shouldApplyMlNotificationDotColors,
  // ]);

  /**
   * Anomaly alert-time marker is disabled for now: it reddened the single point
   * nearest `alertStartTs`, which marks when the insight fired rather than which
   * reading was anomalous. Restore by taking `pointTs` again and returning
   * `pointTs === alertMarkerTs ? '#FF0000' : '#F59E0B'` when
   * `shouldApplyMlNotificationDotColors` is set.
   */
  const getDotColor = (pointValue?: number) => {
    if (shouldApplyNotificationDotColors) {
      return doesPointMatchRuleCondition(pointValue) ? '#FF0000' : '#F59E0B';
    }

    return '#F59E0B';
  };
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const xDomain = (() => {
    if (processedChartData.length === 0) return undefined;
    if (processedChartData.length === 1) {
      const t = processedChartData[0].ts;
      const pad = 5 * 60 * 1000;
      return [t - pad, t + pad] as [number, number];
    }
    const tsValues = processedChartData.map((d) => d.ts);
    const dataMin = Math.min(...tsValues);
    const dataMax = Math.max(...tsValues);
    const range = dataMax - dataMin;
    if (range === 0) {
      const pad = 60 * 1000;
      return [dataMin - pad, dataMax + pad] as [number, number];
    }
    return [dataMin, dataMax] as [number, number];
  })();
  const xAxisTickValues = (() => {
    if (!xDomain) return undefined;
    const [start, end] = xDomain;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return undefined;
    }
    const desiredTickCount = isSmallScreen ? 4 : 8;
    const step = (end - start) / (desiredTickCount - 1);
    return Array.from({ length: desiredTickCount }, (_, index) =>
      Math.round(start + step * index)
    );
  })();

  // Disabled with the alert-time band below — kept so the band can be restored
  // without re-deriving the domain clamping.
  // const highlightWindow = (() => {
  //   /**
  //    * Threshold insights render one chart per sensor tag, and their evidence
  //    * window is already tightly centred on the alert, so a band adds noise
  //    * without adding information. Only anomaly-detection charts get one.
  //    */
  //   const isPerTagThresholdChart =
  //     chartTagName != null && chartTagName.trim().length > 0;
  //   if (isPerTagThresholdChart) {
  //     return null;
  //   }
  //
  //   // The band sits on the same display-shifted axis as the plotted points.
  //   const toDisplayTs = (utcMs: number) =>
  //     utcMs + displayOffsetMs(utcMs, timezone);
  //
  //   /**
  //    * Data often starts after the queried window opens, so a band anchored on
  //    * the alert can extend past the plotted range. Clamp it to the axis domain
  //    * and keep whatever overlaps, instead of letting it fall off-canvas.
  //    */
  //   const clampToDomain = (x1: number, x2: number) => {
  //     if (!xDomain) return { x1, x2 };
  //     const [domainMin, domainMax] = xDomain;
  //     const visibleFrom = Math.max(x1, domainMin);
  //     const visibleTo = Math.min(x2, domainMax);
  //     return visibleTo > visibleFrom
  //       ? { x1: visibleFrom, x2: visibleTo }
  //       : null;
  //   };
  //
  //   const alertUtcMs = evidenceTimestampToUtcMs(alertStartTs);
  //   if (!alertStartTs || Number.isNaN(alertUtcMs)) {
  //     return null;
  //   }
  //   /**
  //    * Always mark a narrow band around the alert start, for both the per-tag
  //    * and the legacy evidence paths. The queried window spans the whole chart,
  //    * so shading it highlights nothing.
  //    */
  //   const halfBandMs = 30 * 60 * 1000;
  //   const alertDisplayTs = toDisplayTs(alertUtcMs);
  //   return clampToDomain(
  //     alertDisplayTs - halfBandMs,
  //     alertDisplayTs + halfBandMs
  //   );
  // })();

  return (
    <Box
      sx={{
        height: '540px',
        backgroundColor: palette.boxBackground,
        borderRadius: '8px',
        border: `1px solid ${
          enableDarkTheme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'
        }`,
        boxShadow: enableDarkTheme
          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'scroll',
      }}
    >
      {isEvidenceLoading && !suppressInternalLoader && (
        <Box sx={{ padding: '5%', height: '100%', marginTop: '20%' }}>
          <SingleFadeLoader isFullScreen={false} />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px 12px 20px',
          borderBottom: `1px solid ${
            enableDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
          }`,
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: '700',
            color: palette.textPrimary,
          }}
        >
          {chartTitle}
        </Typography>
        {/*
          Two-series charts need naming: without it the amber and green lines
          are just colours. Only shown when the bound series is actually
          plotted, so single-series charts keep their plain header.
        */}
        {hasBoundSeries && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            {[
              { color: '#F59E0B', label: t('actual_trend') },
              { color: BOUND_COLOR, label: t('predicted_trend') },
            ].map((entry) => (
              <Box
                key={entry.label}
                sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {/* Line plus dot, mirroring how the series is actually drawn. */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '26px',
                    height: '4px',
                    borderRadius: '2px',
                    backgroundColor: entry.color,
                    flexShrink: 0,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '9px',
                      height: '9px',
                      borderRadius: '50%',
                      backgroundColor: entry.color,
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: axisTickColor,
                  }}
                >
                  {entry.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {shouldApplyNotificationDotColors && (
          <Typography
            sx={{
              fontSize: '11px',
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.lightGrey,
            }}
          >
            {t('rule_condition_data')}{' '}
            {matchedConditionsForChartTag.length > 0
              ? 'Available'
              : 'Not available'}
          </Typography>
        )}

        {onToggleDetailChart && (
          <IconButton
            onClick={onToggleDetailChart}
            sx={{
              padding: '4px',
              color: palette.textPrimary,
              '&:hover': {
                backgroundColor: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
              transform: showDetailChart ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s ease-in-out',
            }}
            title={showDetailChart ? 'Hide detail chart' : 'Show detail chart'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          padding: '20px',
          height: 'calc(100% - 65px)',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={processedChartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={
                enableDarkTheme ? palette.cartesianGrid : palette.cartesianGrid
              }
            />
            {/*
              Alert-time band and marker line disabled for now. Both shaded
              `alertStartTs` +/- 30 min, i.e. when the insight fired, not which
              readings were anomalous. Re-enable by uncommenting; the
              `highlightWindow` and `alertMarkerTs` values are still computed.

              {highlightWindow && (
                <ReferenceArea
                  x1={highlightWindow.x1}
                  x2={highlightWindow.x2}
                  fill="#EF4444"
                  fillOpacity={enableDarkTheme ? 0.18 : 0.12}
                  stroke="#EF4444"
                  strokeOpacity={0.55}
                  strokeDasharray="4 3"
                  ifOverflow="hidden"
                />
              )}
              {alertMarkerTs != null && (
                <ReferenceLine
                  x={alertMarkerTs}
                  stroke="#EF4444"
                  strokeWidth={1.5}
                  ifOverflow="hidden"
                />
              )}
            */}
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={xDomain ?? ['dataMin', 'dataMax']}
              ticks={xAxisTickValues}
              tickFormatter={(value) =>
                // `ts` already carries the display offset, so format it as-is —
                // converting again would shift it a second time.
                dayjs.utc(value).format('MM-DD HH:mm')
              }
              tick={{ fontSize: 10, fill: axisTickColor, fontWeight: 600 }}
              stroke={axisLineColor}
              minTickGap={40}
              interval={0}
            />
            <YAxis
              dataKey="value"
              tick={{ fontSize: 10, fill: axisTickColor, fontWeight: 600 }}
              stroke={axisLineColor}
              label={{
                value:
                  insightValue === 'transmission_inefficiency'
                    ? 'transmission speed / engine rpm'
                    : `${String(yAxisMetricLabel)}${valueUnitSuffix}`,
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: '10px', fill: axisTickColor },
              }}
            />
            <Tooltip
              content={
                <CustomTooltip
                  enableDarkTheme={enableDarkTheme}
                  onPointClick={onPointClick}
                  // The axis plots in the selected site timezone, so pin the
                  // tooltip to it too rather than the user's local preference.
                  preferredTimeDisplayMode="configured"
                  timezone={timezone}
                  valueUnitSuffix={valueUnitSuffix}
                  evidenceQuantityKind={evidenceQuantityKind}
                />
              }
            />
            {/*
              Margin between the reading and its predicted bound. Recharts has
              no band primitive, so it is drawn as two stacked areas: an
              invisible baseline at the lower of the two values, then the
              distance between them shaded on top. Rendered before the reading
              and bound series so their lines and dots stay crisp on top of it.
            */}
            {hasBoundSeries && (
              <defs>
                <pattern
                  id={gapPatternId}
                  width={8}
                  height={8}
                  patternTransform="rotate(45)"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    width={8}
                    height={8}
                    fill={GAP_COLOR}
                    fillOpacity={enableDarkTheme ? 0.26 : 0.18}
                  />
                  <line
                    x1={0}
                    y1={0}
                    x2={0}
                    y2={8}
                    stroke={GAP_COLOR}
                    strokeWidth={2.5}
                    strokeOpacity={enableDarkTheme ? 0.55 : 0.4}
                  />
                </pattern>
              </defs>
            )}
            {hasBoundSeries && (
              <Area
                type="monotone"
                dataKey="gapBase"
                stackId="boundGap"
                stroke="none"
                fill="none"
                fillOpacity={0}
                isAnimationActive={false}
                activeDot={false}
                dot={false}
                legendType="none"
                tooltipType="none"
                connectNulls={false}
              />
            )}
            {hasBoundSeries && (
              <Area
                type="monotone"
                dataKey="gapSpan"
                stackId="boundGap"
                stroke="none"
                // Hatching keeps the band legible over the amber area fill
                // without needing the opacity that would wash out the series.
                fill={`url(#${gapPatternId})`}
                isAnimationActive={false}
                activeDot={false}
                dot={false}
                legendType="none"
                tooltipType="none"
                connectNulls={false}
              />
            )}
            {hasBoundSeries && (
              <Area
                type="monotone"
                dataKey="bound"
                name="Predicted bound"
                stroke={BOUND_COLOR}
                strokeWidth={2}
                fill="none"
                fillOpacity={0}
                connectNulls={false}
                isAnimationActive={false}
                activeDot={false}
                dot={(props: RechartsPayload) => {
                  const { cx, cy, payload } = props;
                  if (
                    cx === null ||
                    cy === null ||
                    payload?.bound === undefined
                  )
                    return <></>;

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={2.5}
                      fill={BOUND_COLOR}
                      stroke={BOUND_COLOR}
                      strokeWidth={1.5}
                    />
                  );
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke="#F59E0B"
              strokeWidth={2}
              fill="#F59E0B"
              /**
               * Thinned when the margin band is present: at 0.3 the amber wash
               * tints the red hatching brown. The fill still reads, and the
               * line and dots above it carry the series.
               */
              fillOpacity={
                insightValue === 'transmission_inefficiency'
                  ? 0
                  : hasBoundSeries
                  ? 0.12
                  : 0.3
              }
              dot={(props: RechartsPayload) => {
                const { cx, cy, payload } = props;
                if (cx === null || cy === null || !payload) return <></>;

                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={3}
                    fill={getDotColor(payload.value)}
                    strokeWidth={2}
                    stroke={getDotColor(payload.value)}
                    onClick={() => {
                      if (getDotColor(payload.value) === '#F59E0B') {
                        if (onPointClick && payload) {
                          onPointClick();
                        }
                      }
                    }}
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                  />
                );
              }}
              activeDot={(props: RechartsPayload) => {
                const { cx, cy, payload } = props;
                if (cx === null || cy === null || !payload) return <></>;

                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill={getDotColor(payload.value)}
                    strokeWidth={2}
                    stroke={getDotColor(payload.value)}
                    onClick={() => {
                      if (getDotColor(payload.value) === '#F59E0B') {
                        if (onPointClick && payload) {
                          onPointClick();
                        }
                      }
                    }}
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                  />
                );
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
